import { AzureDataExplorerDataset, IAttribute, Metamodel, format, IViolation, ParameterConfigUtils } from 'shell-models-lib';
import { Constants } from 'app/shared/shared-constants';
import { CopySource } from './copysource';
import { ParameterConfig } from 'app/model/pipeline/parameter-config';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { stringIsNullOrEmpty } from 'app/shared/utils/string.util';

type CopySourceMode = 'table' | 'query' | 'function';

const functionParametersValidator = ParameterConfigUtils.createParameterConfigurationValidator(false, null, false);

@Metamodel.model({
    name: adfModelTypeMap.azureDataExplorer.copySourceType,
    display: [
        format(stringResources.ModelName_CopySourceTemplate, stringResources.AzureDataExplorer_DisplayText),
        format(stringResources.ModelName_CopySourcesTemplate, stringResources.AzureDataExplorer_DisplayText)
    ],
    tags: { datasetModel: Metamodel.modelFor(AzureDataExplorerDataset) }
})
export class AzureDataExplorerCopySource extends CopySource {
    public static readonly QUERY_TIMEOUT: IAttribute;
    public static readonly QUERY: IAttribute;
    public static readonly NO_TRUNCATION: IAttribute;
    public static readonly FUNCTION_NAME: IAttribute;
    public static readonly FUNCTION_PARAMETERS: IAttribute;

    @Metamodel.attribute({
        templatable: true,
        association: 'QUERY',

        constraint: {
            validator: AzureDataExplorerCopySource.validateQuery
        },
        display: stringResources.QueryLabel
    })
    public query: string;

    @Metamodel.attribute({
        templatable: true,
        association: 'FUNCTION_NAME',

        constraint: {
            validator: AzureDataExplorerCopySource.validateFunctionName
        },
        bypassExpressionValidation: (source) => (source as AzureDataExplorerCopySource).mode !== 'function',
        display: stringResources.ModelName_FunctionName
    })
    public functionName?: string;

    @Metamodel.attribute({
        association: 'FUNCTION_PARAMETERS',
        accepts: ['Object', 'Array'],

        constraint: {
            validator: AzureDataExplorerCopySource.validateFunctionParameters
        },
        display: stringResources.Parameters
    })
    public functionParameters?: ParameterConfig[] = [];

    @Metamodel.attribute({
        templatable: true,
        association: 'QUERY_TIMEOUT',

        constraint: {
            pattern: Constants.writeTimeoutValidation
        },
        display: stringResources.ModelName_Source_QueryTimeout
    })
    public queryTimeout?: string = '01:00:00';

    @Metamodel.attribute({
        templatable: true,
        association: 'NO_TRUNCATION',

        display: stringResources.ModelName_NoTruncation
    })
    public noTruncation?: boolean = true;

    public mode?: CopySourceMode = 'query';

    public static validateQuery(adxCopySource: AzureDataExplorerCopySource, query: string): IViolation[] | undefined {
        if (stringIsNullOrEmpty(query) && (!__IS_FABRIC_DPE__ || adxCopySource.mode === 'query')) {
            const entityDisplayText =  Metamodel.modelOf(adxCopySource).display[0] || format(stringResources.ModelName_CopySourceTemplate, stringResources.AzureDataExplorer_DisplayText);
            return [{
                context: adxCopySource.query,
                messageType: 'missing',
                logDetails: {
                    functionName: 'AzureDataExplorerCopySource.validateQuery',
                    errorType: 'Required',
                    details: { activityName: adxCopySource.owner?.named?.name, mode: adxCopySource.mode }
                },
                message: () => format(stringResources.RequiredValidatorMessageNoVowel, stringResources.QueryLabel, entityDisplayText, '')
            }];
        }
        return undefined;
    }

    public static validateFunctionName(adxCopySource: AzureDataExplorerCopySource, functionName: string): IViolation[] | undefined {
        if (adxCopySource.mode === 'function' && stringIsNullOrEmpty(functionName)) {
            const entityDisplayText = Metamodel.modelOf(adxCopySource).display[0] || format(stringResources.ModelName_CopySourceTemplate, stringResources.AzureDataExplorer_DisplayText);
            return [{
                context: adxCopySource.functionName,
                messageType: 'missing',
                logDetails: {
                    functionName: 'AzureDataExplorerCopySource.validateFunctionName',
                    errorType: 'Required',
                    details: { activityName: adxCopySource.owner?.named?.name }
                },
                message: () => format(stringResources.RequiredValidatorMessageNoVowel, stringResources.ModelName_FunctionName, entityDisplayText, '')
            }];
        }
        return undefined;
    }

    public static validateFunctionParameters(
        adxCopySource: AzureDataExplorerCopySource,
        parameters: ParameterConfig[],
        violation?: IViolation
    ): IViolation[] | undefined {
        return adxCopySource.mode === 'function' ? functionParametersValidator(adxCopySource, parameters, violation) : undefined;
    }
}
